<template>
  <v-dialog
    :value="visible"
    @input="$emit('update:visible', $event)"
    max-width="1100px"
    scrollable
    persistent
  >
    <v-card class="traceability-modal rounded-xl">
      <!-- Header -->
      <div class="traceability-header">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <div class="header-icon-wrap mr-3">
              <v-icon color="#ffffff" size="24">mdi-source-branch</v-icon>
            </div>
            <div>
              <h3 class="header-title">
                Historial de Trazabilidad y Ampliaciones
              </h3>
              <p class="header-subtitle mb-0">
                Cadena genealógica de requerimientos y variaciones de vacantes
              </p>
            </div>
          </div>
          <v-btn icon dark @click="close" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content -->
      <v-card-text class="traceability-body pa-5">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="#3b5bdb" size="48" />
          <p class="text-caption grey--text text--darken-1 mt-3">
            Cargando historial de trazabilidad...
          </p>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!treeItems || treeItems.length === 0"
          class="empty-state py-10 text-center"
        >
          <v-icon size="48" color="#94a3b8">mdi-information-outline</v-icon>
          <p class="text-subtitle-1 font-weight-medium grey--text text--darken-2 mt-2">
            No se encontró historial genealógico para este requerimiento.
          </p>
        </div>

        <!-- Main Tree Timeline -->
        <div v-else class="timeline-container">
          <!-- Summary bar -->
          <div class="summary-banner mb-6">
            <div class="summary-item">
              <span class="summary-label">Campaña</span>
              <span class="summary-value">{{ rootItem.CmpName || "-" }}</span>
            </div>
            <v-divider vertical class="mx-3" />
            <div class="summary-item">
              <span class="summary-label">Cargo</span>
              <span class="summary-value">{{ rootItem.PstName || "-" }}</span>
            </div>
            <v-divider vertical class="mx-3" />
            <div class="summary-item">
              <span class="summary-label">Vacantes Iniciales</span>
              <span class="summary-value font-weight-bold primary--text">
                {{ rootItem.ReqQuantity }}
              </span>
            </div>
            <v-divider vertical class="mx-3" />
            <div class="summary-item">
              <span class="summary-label">Vacantes Vigentes</span>
              <span class="summary-value font-weight-bold text-success-custom">
                {{ latestItem.ReqQuantity }}
              </span>
            </div>
            <v-divider vertical class="mx-3" />
            <div class="summary-item">
              <span class="summary-label">Ampliaciones Totales</span>
              <span class="summary-value font-weight-bold purple--text">
                +{{ totalIncrement }} vacantes
              </span>
            </div>
          </div>

          <!-- Tree Nodes -->
          <div class="tree-chain">
            <div
              v-for="(node, index) in treeItems"
              :key="node.ReqID"
              class="tree-node"
              :class="{
                'tree-node--root': node.Level === 0,
                'tree-node--current': node.ReqID === reqId,
                'tree-node--latest': index === treeItems.length - 1,
              }"
            >
              <!-- Timeline Marker -->
              <div class="node-marker-wrap">
                <div class="node-marker" :class="getMarkerClass(node)">
                  <v-icon x-small color="white">
                    {{ node.Level === 0 ? "mdi-flag-variant" : "mdi-plus-box" }}
                  </v-icon>
                </div>
                <div
                  v-if="index < treeItems.length - 1"
                  class="node-connector"
                />
              </div>

              <!-- Node Card -->
              <div class="node-card">
                <div class="node-card-header">
                  <div class="d-flex align-center flex-wrap gap-2">
                    <span class="node-badge" :class="getBadgeClass(node)">
                      {{ node.Level === 0 ? "Requerimiento Origen" : `Ampliación #${node.Level}` }}
                    </span>
                    <span class="node-code font-weight-bold">
                      #REQ-{{ node.ReqID }}
                    </span>
                    <v-chip
                      x-small
                      :color="getStatusChipColor(node.ReqStatus)"
                      text-color="white"
                      class="font-weight-medium"
                    >
                      {{ node.ReqStatusName || "En Creación" }}
                    </v-chip>
                  </div>
                  <div class="node-date text-caption grey--text">
                    <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                    {{ formatDate(node.SecCreate) }}
                  </div>
                </div>

                <!-- Card Body -->
                <div class="node-card-body mt-3">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <div class="info-block">
                        <span class="info-label">Solicitado por</span>
                        <span class="info-value font-weight-medium">
                          {{ node.UsrCreateName || "Usuario" }}
                        </span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="info-block">
                        <span class="info-label">Tipo / Cobertura</span>
                        <span class="info-value">
                          {{ node.TypeRequirementName || "-" }} ({{ node.TypeSeekingName || "Normal" }})
                        </span>
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="info-block">
                        <span class="info-label">Vacantes</span>
                        <div class="d-flex align-center">
                          <span class="vacancies-total font-weight-bold text-h6">
                            {{ node.ReqQuantity }}
                          </span>
                          <span
                            v-if="node.Level > 0"
                            class="vacancies-delta ml-2"
                          >
                            (+{{ node.IncrementalQuantity }} vacantes)
                          </span>
                          <span
                            v-else
                            class="vacancies-delta ml-2 grey--text"
                          >
                            (Inicial)
                          </span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Observation / Comments -->
                  <div
                    v-if="node.ReqObservation"
                    class="node-observation mt-3"
                  >
                    <v-icon x-small color="#64748b" class="mr-1">
                      mdi-comment-text-outline
                    </v-icon>
                    <span class="font-weight-medium">Comentario:</span>
                    <span class="ml-1 text-body-2 text--secondary">
                      {{ node.ReqObservation }}
                    </span>
                  </div>

                  <!-- Details Accordion (Approvals & Projections) -->
                  <v-expansion-panels flat class="mt-3 node-panels" accordion>
                    <!-- Aprobadores Panel -->
                    <v-expansion-panel
                      v-if="parseApprovals(node.ApprovalsJson).length > 0"
                    >
                      <v-expansion-panel-header class="py-1 px-3 text-caption font-weight-bold grey--text text--darken-2">
                        <span>
                          <v-icon x-small color="#3b5bdb" class="mr-1">
                            mdi-account-check-outline
                          </v-icon>
                          Circuito de Aprobaciones ({{ parseApprovals(node.ApprovalsJson).length }})
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="px-0 pt-2">
                        <div class="approvals-grid">
                          <div
                            v-for="app in parseApprovals(node.ApprovalsJson)"
                            :key="app.RapID"
                            class="approval-mini-card"
                          >
                            <div class="d-flex justify-space-between align-center">
                              <span class="text-caption font-weight-bold">
                                {{ app.ApprovalPstName || "Aprobador" }}
                              </span>
                              <v-chip
                                x-small
                                :color="getApprovalStatusColor(app.RapStatus)"
                                text-color="white"
                              >
                                {{ app.RapStatusName || "Pendiente" }}
                              </v-chip>
                            </div>
                            <div class="text-caption grey--text text--darken-1 mt-1">
                              {{ app.ApprovalPersonName || "-" }}
                            </div>
                            <div
                              v-if="app.RapObservation"
                              class="text-caption text-italic grey--text mt-1"
                            >
                              "{{ app.RapObservation }}"
                            </div>
                          </div>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <!-- Proyecciones / Cronograma Panel -->
                    <v-expansion-panel
                      v-if="
                        parseProjections(node.ProjectionsJson).length > 0 ||
                        parseSchedules(node.SchedulesJson).length > 0
                      "
                    >
                      <v-expansion-panel-header class="py-1 px-3 text-caption font-weight-bold grey--text text--darken-2">
                        <span>
                          <v-icon x-small color="#0d9488" class="mr-1">
                            mdi-calendar-multiselect
                          </v-icon>
                          Distribución de Personal
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="px-0 pt-2">
                        <!-- Masivo / Proyecciones Semanales -->
                        <div
                          v-if="parseProjections(node.ProjectionsJson).length > 0"
                          class="table-responsive"
                        >
                          <table class="mini-table">
                            <thead>
                              <tr>
                                <th>Semana</th>
                                <th>Turno</th>
                                <th>Total Proyectado</th>
                                <th>Hombres</th>
                                <th>Mujeres</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="p in parseProjections(node.ProjectionsJson)"
                                :key="p.WprID"
                              >
                                <td>Semana {{ p.PdsID }}</td>
                                <td>{{ p.TypeTurnName || (p.TypeTurn === 1 ? 'Diurno' : 'Nocturno') }}</td>
                                <td class="font-weight-bold">{{ p.WprQuantityProject }}</td>
                                <td>{{ p.WprQuantityMale }}</td>
                                <td>{{ p.WprQuantityFemale }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <!-- Profesional / Fechas Estimadas -->
                        <div
                          v-if="parseSchedules(node.SchedulesJson).length > 0"
                          class="table-responsive mt-2"
                        >
                          <table class="mini-table">
                            <thead>
                              <tr>
                                <th>Fecha Estimada de Ingreso</th>
                                <th>Cantidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="s in parseSchedules(node.SchedulesJson)"
                                :key="s.PscID"
                              >
                                <td>{{ formatDate(s.PscEstimatedDate) }}</td>
                                <td class="font-weight-bold">{{ s.PscQuantityProject }} vacantes</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Footer -->
      <v-divider />
      <v-card-actions class="pa-4 bg-light">
        <v-spacer />
        <v-btn text color="grey darken-2" @click="close">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _sHmnStaffingRequirement from "@/services/HumanResource/RecruitmentSelection/HmnStaffingRequirement.js";

export default {
  name: "HmnStaffingRequirementTraceabilityModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    reqId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      treeItems: [],
    };
  },
  watch: {
    visible(val) {
      if (val && this.reqId) {
        this.loadTree();
      }
    },
  },
  computed: {
    rootItem() {
      return this.treeItems && this.treeItems.length > 0
        ? this.treeItems[0]
        : {};
    },
    latestItem() {
      return this.treeItems && this.treeItems.length > 0
        ? this.treeItems[this.treeItems.length - 1]
        : {};
    },
    totalIncrement() {
      if (!this.treeItems || this.treeItems.length <= 1) return 0;
      const rootQty = Number(this.rootItem.ReqQuantity) || 0;
      const latestQty = Number(this.latestItem.ReqQuantity) || 0;
      return Math.max(0, latestQty - rootQty);
    },
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    async loadTree() {
      this.loading = true;
      try {
        const resp = await _sHmnStaffingRequirement.traceabilityTree(
          this.reqId,
          this.$fun.getUserID(),
        );
        if (resp.status === 200 && resp.data) {
          this.treeItems = resp.data;
        } else {
          this.treeItems = [];
        }
      } catch (err) {
        this.$fun.alert("Error al cargar la trazabilidad del requerimiento", "error");
        this.treeItems = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return "-";
      return this.$fun.formatDateView(date);
    },
    getMarkerClass(node) {
      if (node.Level === 0) return "marker-root";
      if (node.ReqID === this.reqId) return "marker-current";
      return "marker-child";
    },
    getBadgeClass(node) {
      if (node.Level === 0) return "badge-root";
      return "badge-child";
    },
    getStatusChipColor(status) {
      const s = Number(status);
      if (s === 5) return "success"; // Aprobado (Verde)
      if (s === 6) return "teal darken-1"; // Cerrado
      if (s === 3) return "error"; // Rechazado (Rojo)
      if (s === 2) return "primary"; // En Aprobación (Azul)
      if (s === 7) return "grey darken-1"; // Anulado
      return "amber darken-2"; // En Creación (Amarillo)
    },
    getApprovalStatusColor(status) {
      const s = Number(status);
      if (s === 4) return "success"; // Aprobado (Verde unificado)
      if (s === 2) return "primary"; // Pendiente
      if (s === 3) return "error"; // Rechazado (Rojo)
      return "grey";
    },
    parseApprovals(json) {
      if (!json) return [];
      try {
        return typeof json === "string" ? JSON.parse(json) : json;
      } catch {
        return [];
      }
    },
    parseProjections(json) {
      if (!json) return [];
      try {
        return typeof json === "string" ? JSON.parse(json) : json;
      } catch {
        return [];
      }
    },
    parseSchedules(json) {
      if (!json) return [];
      try {
        return typeof json === "string" ? JSON.parse(json) : json;
      } catch {
        return [];
      }
    },
  },
};
</script>

<style scoped>
.traceability-modal {
  background: #ffffff;
  overflow: hidden;
}

.traceability-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #ffffff;
  padding: 16px 20px;
}

.header-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

.header-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.summary-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.summary-value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
}

.timeline-container {
  padding: 8px 4px;
}

.tree-chain {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tree-node {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.node-marker-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  margin-right: 14px;
  flex-shrink: 0;
}

.node-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  z-index: 2;
}

.marker-root {
  background: #3b5bdb;
}

.marker-child {
  background: #0d9488;
}

.marker-current {
  background: #f59e0b;
  outline: 3px solid #fde68a;
}

.node-connector {
  width: 2px;
  background: #cbd5e1;
  flex-grow: 1;
  min-height: 40px;
  margin-top: 4px;
}

.node-card {
  flex-grow: 1;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  transition: transform 0.15s, box-shadow 0.15s;
}

.tree-node--current .node-card {
  border-color: #f59e0b;
  background: #fffdfa;
}

.node-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 8px;
}

.node-badge {
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.badge-root {
  background: #eff6ff;
  color: #1d4ed8;
}

.badge-child {
  background: #f0fdf4;
  color: #15803d;
}

.info-block {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
}

.info-value {
  font-size: 13px;
  color: #1e293b;
}

.vacancies-total {
  color: #0f172a;
}

.vacancies-delta {
  font-size: 12px;
  font-weight: 700;
  color: #059669;
}

.node-observation {
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #64748b;
  font-size: 12px;
}

.node-panels {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.approvals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

.approval-mini-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.mini-table th,
.mini-table td {
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.mini-table th {
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
  font-size: 11px;
}

.text-success-custom {
  color: #059669 !important;
}
</style>
